import { api } from "@/lib/apiClient";
import type { Booking } from "@/types/camping";

export interface PitchOptionProblem {
  kind: string;
  message: string;
  booking_id: string | null;
  booking_number: string | null;
}

export interface PitchOptionSolution {
  kind: string;
  message: string;
  booking_id: string | null;
}

export type PitchAvailabilityKind = "immediate" | "with_relocation" | "blocked";

export type PitchUtilizationLabel = "optimal" | "good" | "acceptable" | "with_relocation";

export interface PitchRelocationStep {
  booking_id: string;
  booking_number: string | null;
  from_pitch_id: string;
  to_pitch_id: string;
  to_pitch_code: string | null;
}

export interface PitchOption {
  pitch_id: string;
  code: string;
  label: string | null;
  selectable: boolean;
  availability_kind: PitchAvailabilityKind;
  utilization_rank: number | null;
  utilization_label: PitchUtilizationLabel | null;
  relocation_plan: PitchRelocationStep[];
  is_current: boolean;
  problems: PitchOptionProblem[];
  solutions: PitchOptionSolution[];
}

export function isPitchWorkable(option: PitchOption): boolean {
  return option.selectable || option.availability_kind === "with_relocation";
}

export function utilizationLabelText(label: PitchUtilizationLabel | null): string | null {
  switch (label) {
    case "optimal":
      return "Beste Auslastung";
    case "good":
      return "Gute Auslastung";
    case "acceptable":
      return "Funktioniert";
    case "with_relocation":
      return "Mit Umplanung";
    default:
      return null;
  }
}

export interface PitchOptionsResponse {
  booking_id: string;
  suggested_pitch_id: string | null;
  options: PitchOption[];
}

export interface AssignPitchOptions {
  reassign_conflicts?: boolean;
  ignore_rules?: boolean;
  ignore_category?: boolean;
  reason?: string;
}

export function fetchPitchOptions(bookingId: string): Promise<PitchOptionsResponse> {
  return api.get<PitchOptionsResponse>(`/api/v1/pitch-assignments/bookings/${bookingId}/options`);
}

export function ensurePitchSuggestion(
  bookingId: string,
  options: { force?: boolean } = {},
): Promise<Booking> {
  return api.post<Booking>(`/api/v1/pitch-assignments/bookings/${bookingId}/ensure-suggestion`, {
    force: options.force ?? false,
  });
}

export function assignPitchWithOptions(
  bookingId: string,
  pitchId: string,
  options: AssignPitchOptions = {},
): Promise<Booking> {
  return api.post<Booking>(`/api/v1/bookings/${bookingId}/assign-pitch`, {
    pitch_id: pitchId,
    reassign_conflicts: options.reassign_conflicts ?? false,
    ignore_rules: options.ignore_rules ?? false,
    ignore_category: options.ignore_category ?? false,
    reason: options.reason?.trim() || undefined,
  });
}

export interface MovePitchOption {
  pitch_id: string;
  code: string;
  label: string | null;
  category_id: string | null;
  category_name: string | null;
  same_category: boolean;
  is_current: boolean;
  available: boolean;
  rule_warning: string | null;
  unavailable_reason: string | null;
}

export interface MoveOptions {
  current_pitch_free: boolean;
  suggested_move_date: string;
  move_date: string;
  departure_date: string;
  pitches: MovePitchOption[];
}

export function fetchMoveOptions(
  bookingId: string,
  query: { departureDate?: string; moveDate?: string } = {},
): Promise<MoveOptions> {
  const params = new URLSearchParams();
  if (query.departureDate) params.set("departure_date", query.departureDate);
  if (query.moveDate) params.set("move_date", query.moveDate);
  const queryString = params.toString();
  const suffix = queryString ? `?${queryString}` : "";
  return api.get<MoveOptions>(`/api/v1/bookings/${bookingId}/move-options${suffix}`);
}

export function relocateStay(
  bookingId: string,
  body: {
    version: number;
    move_date: string;
    pitch_id: string;
    departure_date?: string;
    reason: string;
    ignore_rules?: boolean;
  },
): Promise<Booking> {
  return api.post<Booking>(`/api/v1/bookings/${bookingId}/relocate`, body);
}

export function unfixPitch(bookingId: string, reason: string): Promise<Booking> {
  return api.post<Booking>(`/api/v1/bookings/${bookingId}/unfix-pitch`, {
    reason: reason.trim(),
  });
}

export { buildAssignOptionsFromSolution } from "@/lib/pitchSolutionConsequences";
